import { useMemo, useState, useEffect } from 'react';
import AccountHierarchy from './AccountHierarchy';
import CreateEditZoneForm from './CreateEditZoneForm';
import PMZoneDetails from './PMZoneDetails';
import { Button, LoadingData } from '@hwm/ui-lib';
import { Trans, t } from '@lingui/macro';
import { useSetAccDataSource } from '@features/dataSource';
import { AccountModel } from '@shared/models';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useSidebar } from '@shared/hooks/useSider';
import { RemovePMZButton } from './PMZoneDetails/RemovePMZ';

export enum SiderKeys {
  accHierarchy = 'accHierarchy',
  pmzCreateForm = 'pmzCreateForm',
  pmzEditForm = 'pmzEditForm',
  pmzDetails = 'pmzDetails',
}

export const useSiderContent = (acc: AccountModel, parentId: number | null) => {
  const { handleSidebarSection } = useSidebar();

  const { setAccData } = useSetAccDataSource();
  const { canEditOrCreate } = usePermissions();

  const siderData = useMemo(
    (): {
      [key in SiderKeys]: {
        header: string;
        buttons: JSX.Element[];
        component: JSX.Element;
      };
    } => ({
      [SiderKeys.accHierarchy]: {
        header: t({
          message: 'PMZ List',
          id: '[Sidebar.PMZoneList]',
          comment: 'Abbreviation from `Pressure Monitoring Zone`',
        }),
        buttons: [
          <Button
            onClick={() => handleSidebarSection(SiderKeys.pmzCreateForm)}
            disabled={acc?.isPmZone || !canEditOrCreate}
            dataTestId="sidebar-header-buttons-create-pmz-button"
          >
            <Trans id="[Action.CreatePMZone]">Create PMZ</Trans>
          </Button>,
        ],
        component: <AccountHierarchy dataTestId="sidebar-accounts-tree-list" />,
      },
      [SiderKeys.pmzCreateForm]: {
        header: t`Create PMZ`,
        buttons: [
          <Button
            variant="outlined"
            onClick={() => {
              setAccData({ accountId: acc?.id?.toString() });
              handleSidebarSection(SiderKeys.accHierarchy);
            }}
            dataTestId="sidebar-header-buttons-close-button"
          >
            <Trans id="[Action.Close]" comment="Close as verb">
              Close
            </Trans>
          </Button>,
        ],
        component: <CreateEditZoneForm typeOfForm="create" dataTestId="sidebar-create-edit-zone" />,
      },
      [SiderKeys.pmzEditForm]: {
        header: t`Edit PMZ`,
        buttons: [
          <Button
            variant="outlined"
            onClick={() => handleSidebarSection(SiderKeys.pmzDetails)}
            dataTestId="sidebar-header-buttons-close-button"
          >
            <Trans id="[Action.Close]" comment="Close as verb">
              Close
            </Trans>
          </Button>,
        ],
        component: <CreateEditZoneForm typeOfForm="edit" dataTestId="sidebar-create-edit-pmz" />,
      },
      [SiderKeys.pmzDetails]: {
        header: t({
          message: 'PMZ Details',
          id: '[Sidebar.PMZDetails]',
          comment: 'Abbreviation from `Pressure Monitoring Zone`',
        }),
        buttons: [
          <Button
            onClick={() => handleSidebarSection(SiderKeys.pmzEditForm)}
            disabled={!acc?.isPmZone || !canEditOrCreate}
            dataTestId="sidebar-header-buttons-edit-button"
          >
            <Trans id="[Action.EditPMZone]">Edit</Trans>
          </Button>,
          <RemovePMZButton disabled={!acc?.isPmZone || !canEditOrCreate} parentId={parentId?.toString()} />,
          <Button
            variant="outlined"
            onClick={() =>
              setAccData({ accountId: parentId?.toString() }).then(() => handleSidebarSection(SiderKeys.accHierarchy))
            }
            dataTestId="sidebar-header-buttons-close-button"
          >
            <Trans id="[Action.Close]" comment="Close as verb">
              Close
            </Trans>
          </Button>,
        ],
        component: <PMZoneDetails dataTestId="sidebar-pmz-details" />,
      },
    }),
    [acc?.id, acc?.isPmZone, parentId, canEditOrCreate, setAccData, handleSidebarSection]
  );

  const loadingData = useMemo(
    () => ({
      header: '',
      buttons: [] as JSX.Element[],
      component: <LoadingData />,
    }),
    []
  );

  return { siderContent: siderData, loadingData };
};

export const useSiderAnimation = (changeToTrue: boolean) => {
  const { sidebarState } = useSidebar();

  const [showData, setShowData] = useState(false);

  useEffect(() => {
    setShowData(false);
    const timeout = setTimeout(() => {
      (sidebarState || changeToTrue) && setShowData(true);
    }, 600);

    return () => {
      clearTimeout(timeout);
    };
  }, [sidebarState, changeToTrue]);

  return { showData };
};
